import crypto from 'crypto';

// CRITICAL: Ensure all spaces and whitespace are preserved in block content
// This function should be used whenever processing block content before saving
export const preserveSpaces = (content) => {
  // DO NOT trim, strip, or modify any whitespace
  // Return content exactly as provided
  if (content === null || content === undefined) {
    return '';
  }
  // Keep content as-is with all spaces preserved
  return String(content);
};

export const BLOCK_TYPES = {
  TEXT: 'text',
  HEADER: 'header',
  SUB_HEADER: 'sub_header',
  SUB_SUB_HEADER: 'sub_sub_header',
  BULLETED_LIST: 'bulleted_list',
  NUMBERED_LIST: 'numbered_list',
  TODO: 'to_do',
  QUOTE: 'quote',
  DIVIDER: 'divider',
  CODE: 'code',
  IMAGE: 'image',
  FILE: 'file',
  LINK: 'link',
  TABLE: 'table',
  COLUMN_LIST: 'column_list',
  COLUMN: 'column',
  CALLOUT: 'callout',
  TOGGLE: 'toggle',
};

export const BLOCK_ICONS = {
  [BLOCK_TYPES.TEXT]: '📝',
  [BLOCK_TYPES.HEADER]: '#️⃣',
  [BLOCK_TYPES.SUB_HEADER]: '##️⃣',
  [BLOCK_TYPES.SUB_SUB_HEADER]: '###️⃣',
  [BLOCK_TYPES.BULLETED_LIST]: '•',
  [BLOCK_TYPES.NUMBERED_LIST]: '1.',
  [BLOCK_TYPES.TODO]: '☐',
  [BLOCK_TYPES.QUOTE]: '❝',
  [BLOCK_TYPES.CODE]: '</>',
  [BLOCK_TYPES.IMAGE]: '🖼️',
  [BLOCK_TYPES.FILE]: '📎',
  [BLOCK_TYPES.LINK]: '🔗',
  [BLOCK_TYPES.DIVIDER]: '―',
  [BLOCK_TYPES.TABLE]: '📊',
  [BLOCK_TYPES.CALLOUT]: '💡',
  [BLOCK_TYPES.TOGGLE]: '▶️',
};

export const createBlock = (type, content = '', properties = {}) => {
  const baseBlock = {
    type,
    properties: {},
    format: {},
    order_index: 0
  };

  if (type !== BLOCK_TYPES.IMAGE &&
    type !== BLOCK_TYPES.FILE &&
    type !== BLOCK_TYPES.LINK) {
    baseBlock.properties.title = [[content]];
  }

  switch (type) {
    case BLOCK_TYPES.HEADER:
    case BLOCK_TYPES.SUB_HEADER:
    case BLOCK_TYPES.SUB_SUB_HEADER:
      baseBlock.format.block_width = '100%';
      break;

    case BLOCK_TYPES.CODE:
      baseBlock.properties.language = properties.language || 'javascript';
      baseBlock.format.block_color = 'gray_background';
      break;

    case BLOCK_TYPES.TODO:
      baseBlock.properties.checked = false;
      break;

    case BLOCK_TYPES.IMAGE:
      baseBlock.properties = {
        source: properties.source || [[content]],
        caption: properties.caption || ''
      };
      baseBlock.format.block_width = properties.width || '100%';
      break;

    case BLOCK_TYPES.FILE:
      baseBlock.properties = {
        source: properties.source || [[content]],
        name: properties.name || '',
        size: properties.size || 0,
        type: properties.type || 'file'
      };
      break;

    case BLOCK_TYPES.LINK:
      baseBlock.properties = {
        link: properties.link || [[content]],
        text: properties.text || content,
        ...properties
      };
      delete baseBlock.properties.title;
      break;

    case BLOCK_TYPES.QUOTE:
      baseBlock.format.block_color = 'gray_background';
      break;

    case BLOCK_TYPES.CALLOUT:
      baseBlock.format.block_color = 'blue_background';
      baseBlock.properties.icon = properties.icon || '💡';
      break;

    default:
      baseBlock.properties = {
        ...baseBlock.properties,
        ...properties
      };
  }

  return baseBlock;
};

export const formatText = (text, format = []) => {
  // Handle null/undefined
  if (text == null) return '';
  
  // Handle empty string
  if (text === '') return '';
  
  // If no formatting, return the text with line breaks
  if (!format || !Array.isArray(format) || format.length === 0) {
    return text.replace(/\n/g, '<br>');
  }
  
  let result = text;
  
  // Create a stack to apply formatting
  const formattingStack = [...format];
  
  // Apply formatting in reverse order (innermost first)
  while (formattingStack.length > 0) {
    const f = formattingStack.pop();
    if (Array.isArray(f) && f.length >= 2) {
      const formatType = f[0];
      const formatValue = f[1];
      
      switch (formatType) {
        case 'b': // bold
          result = `<strong>${result}</strong>`;
          break;
        case 'i': // italic
          result = `<em>${result}</em>`;
          break;
        case '_': // underline
          result = `<u>${result}</u>`;
          break;
        case 'c': // code
          result = `<code>${result}</code>`;
          break;
        case 'a': // link
          if (formatValue && formatValue[0] && formatValue[0][0]) {
            result = `<a href="${formatValue[0][0]}" target="_blank" rel="noopener noreferrer">${result}</a>`;
          }
          break;
        case 's': // strikethrough
          result = `<s>${result}</s>`;
          break;
        default:
          // Unknown format, preserve as is
          break;
      }
    }
  }
  
  // Replace newlines with breaks
  result = result.replace(/\n/g, '<br>');
  
  return result;
};

export const getBlockIcon = (type) => {
  return BLOCK_ICONS[type] || BLOCK_ICONS[BLOCK_TYPES.TEXT];
};

export const isContainerBlock = (type) => {
  return [
    BLOCK_TYPES.BULLETED_LIST,
    BLOCK_TYPES.NUMBERED_LIST,
    BLOCK_TYPES.TODO,
    BLOCK_TYPES.TOGGLE,
    BLOCK_TYPES.COLUMN_LIST
  ].includes(type);
};